import { Component, DestroyRef, HostBinding, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, NavigationStart, Router, RouterOutlet } from '@angular/router';
import { Meta } from '@angular/platform-browser';
import { SwUpdate } from '@angular/service-worker';
import { TranslateModule } from '@ngx-translate/core';
import { filter } from 'rxjs/operators';

import { FooterComponent } from './page-elements/footer/footer.component';
import { NavBarComponent } from './page-elements/nav-bar/nav-bar.component';
import { FormModalComponent } from './modal/form-modal/form-modal.component';
import { ChatBotComponent } from './widgets/chat-bot/chat-bot.component';
import { routeAnimations } from './route-animations';
import { IdleTimeoutService } from './services/idle-timeout.service';
import { LanguageService } from './services/language.service';

const PRIVATE_URL = /^(?:\/(?:[a-z]{2}(?:-[a-z]{2})?)?)?\/dashboard\b/i;
const SOUNDS_VIDEO_URL = /\/soundsvideo\b/i;
const AUTH_OR_404_URL = /\/(?:[a-z]{2}(?:-[a-z]{2})?)?\/?(signup|login|admin-login|admin-signup|admin|404)\b/i;
const ADMIN_URL = /\/(?:[a-z]{2}(?:-[a-z]{2})?)?\/?(admin-login|admin-signup|admin)\b/i;

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    FooterComponent,
    NavBarComponent,
    TranslateModule,
    FormModalComponent,
    ChatBotComponent,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  animations: [routeAnimations],
})
export class AppComponent {
  private readonly router = inject(Router);
  private readonly meta = inject(Meta);
  private readonly destroyRef = inject(DestroyRef);
  private readonly swUpdate = inject(SwUpdate, { optional: true });
  // Inject services so their constructors run (side-effects: idle monitoring, language bootstrap)
  private readonly idleTimeout = inject(IdleTimeoutService);
  private readonly languageService = inject(LanguageService);

  readonly isDashboardRoute = signal(false);
  readonly isShellHidden = signal(false);

  @HostBinding('class.route-animating') routeAnimating = false;

  constructor() {
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd || e instanceof NavigationStart),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        if (event instanceof NavigationStart) {
          this.routeAnimating = true;
          return;
        }

        // NavigationEnd
        const url = (event as NavigationEnd).urlAfterRedirects || (event as NavigationEnd).url || '';
        const isPrivate = PRIVATE_URL.test(url);
        const isSoundsVideo = SOUNDS_VIDEO_URL.test(url);
        const hideForAuthOr404 = AUTH_OR_404_URL.test(url);

        this.isDashboardRoute.set(isPrivate);
        this.isShellHidden.set(isPrivate || hideForAuthOr404 || isSoundsVideo);

        if (ADMIN_URL.test(url)) {
          this.meta.updateTag({ name: 'robots', content: 'noindex, nofollow' });
        }

        setTimeout(() => (this.routeAnimating = false), 250);
      });

    this.registerSwUpdates();
  }

  prepareRoute(outlet: RouterOutlet): string {
    return outlet?.activatedRouteData?.['animation'] || '';
  }

  private registerSwUpdates(): void {
    if (!this.swUpdate?.isEnabled) {
      return;
    }
    this.swUpdate.versionUpdates
      .pipe(
        filter((e) => e.type === 'VERSION_READY'),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        if (confirm('Une nouvelle version d\u2019Applisound est disponible. Recharger maintenant ?')) {
          document.location.reload();
        }
      });
  }
}
